import React from 'react';

/**
 * The body height the notes and caregiver-load panels both pin themselves to, so the two cards
 * line up and the map below them never moves as data or filters change.
 */
export const DASHBOARD_PANEL_BODY_HEIGHT = 340;

type PanelProps = {
  title?: React.ReactNode;
  /** Rendered on the right of the header row — counts, segmented controls, links. */
  extra?: React.ReactNode;
  className?: string;
  /** Padding for the body. Panels holding a table or a scroll area usually want `px-4 pb-4`. */
  pad?: string;
  /** Fixed height, in px, for the body's content — for panels that must match a neighbour. */
  bodyHeight?: number;
  /** Fill the parent's height instead (a dashboard widget); `bodyHeight` is then ignored. */
  fill?: boolean;
  children: React.ReactNode;
};

/**
 * The dashboard's card shell. Header is omitted entirely when there is no title and no extra,
 * so a Panel can also be used as a plain rounded container.
 */
const Panel: React.FC<PanelProps> = ({ title, extra, className = '', pad = 'p-4', bodyHeight, fill, children }) => (
  <div
    className={
      // A size container, so a panel's controls can adapt to its own width (it may be a resizable widget).
      '@container flex min-w-0 flex-col rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,.04)] ' +
      (fill ? 'h-full overflow-hidden ' : '') +
      className
    }
  >
    {(title || extra) && (
      // Controls drop onto their own line under the title when the panel is too narrow for one row.
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 pb-2 pt-3.5">
        <div className="min-w-0 text-[15px] font-semibold text-slate-800">{title}</div>
        {extra && <div className="flex min-w-0 max-w-full flex-wrap items-center gap-x-4 gap-y-2">{extra}</div>}
      </div>
    )}
    <div className={'min-h-0 flex-1 ' + pad + (title ? ' pt-1' : '') + (fill ? ' flex flex-col overflow-auto' : '')}>
      {fill ? (
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      ) : bodyHeight === undefined ? (
        children
      ) : (
        // Inside the padding rather than on it, so the height is the space the children get.
        <div className="flex min-h-0 flex-col" style={{ height: bodyHeight }}>
          {children}
        </div>
      )}
    </div>
  </div>
);

export default Panel;
